import jsPDF from "jspdf";
import { pdfSafe } from "./pdfText";

export interface AnswerKeyQuestion {
  index: number;
  topic: string;
  question: string;
  answer: string;
  /** Optional reasoning sentence explaining HOW to arrive at the answer. */
  reasoning?: string;
}

export interface AnswerKeyMeta {
  difficulty: string;
  topics: string;
  date: string;
}

/**
 * Render a teacher-facing answer key with reasoning. Client-side only, so no
 * worksheet content ever leaves the device.
 */
export function downloadAnswerKeyPdf(
  questions: AnswerKeyQuestion[],
  meta: AnswerKeyMeta,
) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 56;
  let y = 0;

  function header() {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("ELEMENTPAD · ANSWER KEY", MARGIN, 32);
    doc.setTextColor(37, 99, 235);
    doc.text(meta.date.toUpperCase(), PAGE_W - MARGIN, 32, { align: "right" });
    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 40, PAGE_W - MARGIN, 40);
  }

  function footer(page: number, total: number) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${page} of ${total}`, PAGE_W - MARGIN, PAGE_H - 24, {
      align: "right",
    });
    doc.text(
      "Generated on-device. Nothing is uploaded.",
      MARGIN,
      PAGE_H - 24,
    );
  }

  function newPage() {
    doc.addPage();
    header();
    y = 70;
  }

  function ensureSpace(lines: number) {
    if (y + lines * 14 > PAGE_H - 60) newPage();
  }

  // Cover info
  header();
  y = 70;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(20);
  doc.text("Worksheet Answer Key", MARGIN, y);
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80);
  const metaWrap = doc.splitTextToSize(
    pdfSafe(`${questions.length} questions · Difficulty: ${meta.difficulty} · Topics: ${meta.topics}`),
    PAGE_W - 2 * MARGIN,
  );
  doc.text(metaWrap, MARGIN, y);
  y += metaWrap.length * 12 + 12;

  doc.setDrawColor(230);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 14;

  questions.forEach((q) => {
    const numLabel = `${q.index}.`;
    const qLines = doc.splitTextToSize(pdfSafe(q.question), PAGE_W - 2 * MARGIN - 24);
    const ansLines = doc.splitTextToSize(pdfSafe(`Answer: ${q.answer}`), PAGE_W - 2 * MARGIN - 24);
    const reasoningLines = q.reasoning
      ? doc.splitTextToSize(pdfSafe(`Reasoning: ${q.reasoning}`), PAGE_W - 2 * MARGIN - 24)
      : [];
    const totalLines = qLines.length + ansLines.length + reasoningLines.length + 2;
    ensureSpace(totalLines);

    // Number
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(37, 99, 235);
    doc.text(numLabel, MARGIN, y);

    // Question
    doc.setFont("helvetica", "normal");
    doc.setTextColor(20);
    doc.text(qLines, MARGIN + 24, y);
    y += qLines.length * 12 + 4;

    // Answer
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 118, 110);
    doc.text(ansLines, MARGIN + 24, y);
    y += ansLines.length * 12 + 2;

    // Reasoning
    if (reasoningLines.length) {
      doc.setFont("helvetica", "italic");
      doc.setTextColor(80);
      doc.text(reasoningLines, MARGIN + 24, y);
      y += reasoningLines.length * 12;
    }

    y += 10;
  });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    footer(i, total);
  }

  doc.save(`elementpad-answer-key.pdf`);
}

/**
 * Heuristic reasoning generator from topic + question + answer. We avoid
 * shipping per-question explanations (the pools are large) by deriving a
 * one-line "how-to" hint from the topic.
 */
export function reasoningFor(
  topic: string,
  question: string,
  answer: string,
): string {
  switch (topic) {
    case "element-id":
      return "Look up the symbol/atomic number on the periodic table; one row per element gives both.";
    case "electron-config":
      return "Fill orbitals in order 1s, 2s, 2p, 3s, 3p, 4s, 3d, ... up to the element's electron count (Aufbau).";
    case "equation-balance":
      return "Balance metals first, then nonmetals (C, H), and oxygen LAST. Use whole-number coefficients only.";
    case "molar-mass":
      return "Sum (atoms × atomic mass) for every element in the formula. Apply subscripts inside parentheses to ALL atoms inside.";
    case "properties":
      return "Read the property directly from the periodic table or element tile.";
    case "sig-figs":
      return "Leading zeros are NOT significant. Captive zeros and trailing zeros after a decimal ARE significant.";
    case "mole-conversion":
      return "Use the mole road map: g ↔ mol via molar mass; mol ↔ L (gas, STP) via 22.4 L/mol; mol ↔ particles via 6.022×10²³.";
    case "ox-number":
      return "Group I = +1, Group II = +2, O = -2 (usually), H = +1 (usually). Sum of ox numbers = compound charge.";
    case "ionic-naming":
      return "Cation name (with Roman numeral if transition metal) + anion name. For formula → name, balance charges to neutral.";
    case "lewis":
      return "Count valence electrons, draw skeleton, place lone pairs, satisfy octets (H = 2). Add multiple bonds if needed.";
    case "stoichiometry":
      return "Balance the equation, convert given to moles, use the mole ratio, then convert moles to the requested unit.";
    default:
      return `Compare your work to the answer: ${answer}.`;
  }
}